/**
 * Continue-register wizard: step order and questions follow Profile → Preferences
 * ([`memberProfilePreferences.ts`](./memberProfilePreferences.ts)); **choices** are
 * built from `GET /member/tags` (tag ids + names).
 */
import type { PreferencesRequest, TagCategory } from "@/types/api";
import { PROFILE_PREFERENCE_CATEGORIES } from "@/constants/memberProfilePreferences";

/** Route path — keep in sync with `routes.ts` and RootLayout gate allowlist. */
export const MEMBER_CONTINUE_REGISTER_PATH = "/onboarding";

/**
 * For each profile row, which key to read on `/member/tags` `data`
 * (see https://api.venuelista.com/api/member/tags).
 */
export const MEMBER_TAGS_BUCKET_BY_PROFILE_ID: Record<string, string> = {
  venues: "venue",
  amenities: "amenity",
  drinks: "specialty",
  cuisines: "cuisine",
  djs: "dj",
  spending: "minprice",
  crowd: "crowd",
  music: "music",
  atmosphere: "atmosphere",
  entertainment: "entertainment",
  dietary: "dietaries",
  payments: "payment",
};

/** `/member/account-tags` + `/member/tags` bucket key → Profile → Preferences category id. */
export const MEMBER_TAGS_BUCKET_TO_PROFILE_ID: Record<string, string> = Object.fromEntries(
  (Object.entries(MEMBER_TAGS_BUCKET_BY_PROFILE_ID) as [string, string][]).map(
    ([profileId, bucket]) => [bucket, profileId]
  )
);

/** Profile category id → `PreferencesRequest` key for POST /member/continue-register */
const PROFILE_ID_TO_API_KEY: Record<string, keyof PreferencesRequest> = {
  venues: "venue",
  amenities: "amenity",
  drinks: "specialty",
  cuisines: "cuisine",
  djs: "dj",
  spending: "minprice",
  crowd: "crowd",
  music: "music",
  atmosphere: "atmosphere",
  entertainment: "entertainment",
  dietary: "dietaries",
  payments: "payment",
};

/** Keys we collect in the wizard (subset of PreferencesRequest). */
export const CONTINUE_REGISTER_PREFERENCE_KEYS = [
  "venue",
  "specialty",
  "cuisine",
  "music",
  "entertainment",
  "dj",
  "amenity",
  "atmosphere",
  "crowd",
  "minprice",
  "dietaries",
  "payment",
] as const satisfies readonly (keyof PreferencesRequest)[];

export type ContinueRegisterCollectedKey = (typeof CONTINUE_REGISTER_PREFERENCE_KEYS)[number];

export interface ContinueRegisterChoice {
  id: number;
  label: string;
}

export type ContinueRegisterStep =
  | {
      kind: "multi";
      question: string;
      preferenceKey: ContinueRegisterCollectedKey;
      choices: ContinueRegisterChoice[];
    }
  | {
      kind: "verification";
      question: string;
    };

/** Build all wizard steps (one multi-select per preferences row) from live `/member/tags`. */
export function buildContinueRegisterSteps(memberTags: TagCategory): ContinueRegisterStep[] {
  const steps: ContinueRegisterStep[] = [];

  for (const cat of PROFILE_PREFERENCE_CATEGORIES) {
    const bucket = MEMBER_TAGS_BUCKET_BY_PROFILE_ID[cat.id];
    const rows = bucket ? memberTags[bucket] : undefined;
    const choices = (rows ?? []).map((t) => ({ id: t.id, label: t.name }));
    const preferenceKey = PROFILE_ID_TO_API_KEY[cat.id] as ContinueRegisterCollectedKey;
    steps.push({
      kind: "multi",
      question: cat.question,
      preferenceKey,
      choices,
    });
  }

  steps.push({
    kind: "verification",
    question: "Verify it's really you",
  });

  return steps;
}

export function emptyContinueRegisterSelections(): Record<ContinueRegisterCollectedKey, number[]> {
  return {
    venue: [],
    specialty: [],
    cuisine: [],
    music: [],
    entertainment: [],
    dj: [],
    amenity: [],
    atmosphere: [],
    crowd: [],
    minprice: [],
    dietaries: [],
    payment: [],
  };
}

/**
 * "Finish later": when a member skips steps we save what they picked, then hold the
 * continue-register redirect until the time they chose. Once it passes, the gate sends
 * them back (wizard is prefilled from `/member/account-tags`).
 * ponytail: per-browser localStorage — move to a member column if reminders must follow devices.
 */
const remindKey = (userId: string | number) => `vl_profile_remind_at_${userId}`;

export const PROFILE_REMIND_OPTIONS = [
  { label: "In 1 hour", ms: 60 * 60 * 1000 },
  { label: "Tomorrow", ms: 24 * 60 * 60 * 1000 },
  { label: "Next week", ms: 7 * 24 * 60 * 60 * 1000 },
] as const;

export function setProfileReminder(userId: string | number, inMs: number | null): void {
  try {
    if (inMs === null) localStorage.removeItem(remindKey(userId));
    else localStorage.setItem(remindKey(userId), String(Date.now() + inMs));
  } catch {
    /* storage blocked — gate falls back to `completed` only */
  }
}

/** Reminder time in ms, or null when none is set. */
function getProfileReminder(userId: string | number): number | null {
  try {
    const raw = localStorage.getItem(remindKey(userId));
    const at = raw ? Number(raw) : NaN;
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

/** Should this member be sent to the continue-register wizard right now? */
export function memberNeedsProfileWizard(user: { id: string | number; completed?: boolean }): boolean {
  const remindAt = getProfileReminder(user.id);
  if (remindAt !== null) return Date.now() >= remindAt;
  return user.completed !== true;
}
